import { SEVERITY_UI } from '@baseline/shared';

import type { Severity, UiStatusKey } from '@baseline/shared';
import type { ReactNode } from 'react';

// SVGs ported from the prototype: the tree-mark logo, nav icons (`NAVI`), status icons
// (`SI`), severity bars (`sevI`), the chevrons and the empty-state mark.

/** The tree mark. `light` is the top bar variant (mint on dark), `dark` the login card one. */
export function Logo({
  size = 30,
  variant = 'light',
}: {
  size?: number;
  variant?: 'light' | 'dark';
}) {
  const fill = variant === 'light' ? '#99F6E4' : '#0F766E';
  return (
    <svg
      className="mk"
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <g transform="translate(2.2 1.6)">
        <g transform="translate(6.5 5.5)" stroke="#2DD4BF" strokeWidth="2">
          <path d="M11 8.5v4.5M11 13H5v3M11 13h6v3" />
          <circle cx="11" cy="6.2" r="2.6" fill="none" />
          <circle cx="5" cy="19" r="2.6" fill="none" />
          <circle cx="17" cy="19" r="2.6" fill="none" />
        </g>
        <g stroke={fill} strokeWidth="2">
          <path d="M11 8.5v4.5M11 13H5v3M11 13h6v3" />
          <circle cx="11" cy="6.2" r="2.6" fill={fill} />
          <circle cx="5" cy="19" r="2.6" fill={fill} />
          <circle cx="17" cy="19" r="2.6" fill={fill} />
        </g>
      </g>
    </svg>
  );
}

/** The empty-state mark (`EMPTY_SVG`): the login-card logo at 64 px. */
export function EmptyMark() {
  return <Logo size={64} variant="dark" />;
}

function Ico({ children, size = 16 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export type NavKey = 'dashboard' | 'alerts' | 'compare' | 'connectors';

export function NavIcon({ name }: { name: NavKey }) {
  switch (name) {
    case 'dashboard':
      return (
        <Ico>
          <rect x="3.5" y="3.5" width="7" height="8" rx="1.5" />
          <rect x="13.5" y="3.5" width="7" height="5" rx="1.5" />
          <rect x="13.5" y="11.5" width="7" height="9" rx="1.5" />
          <rect x="3.5" y="14.5" width="7" height="6" rx="1.5" />
        </Ico>
      );
    case 'alerts':
      return (
        <Ico>
          <path d="M4 6h16M4 12h16M4 18h10" />
        </Ico>
      );
    case 'compare':
      return (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M8 1v14" />
          <path d="M2 4h4M2 8h4M10 4h4M10 8h4M10 12h4" />
        </svg>
      );
    case 'connectors':
      return (
        <Ico>
          <path d="M9 3v5M15 3v5" />
          <path d="M6 8h12v4a6 6 0 0 1-12 0z" />
          <path d="M12 18v3" />
        </Ico>
      );
  }
}

type StatusIconKey = Exclude<UiStatusKey, 'needs_decision'>;

/** Prototype `SI`. */
export function StatusIcon({ status }: { status: StatusIconKey }) {
  const common = {
    width: 14,
    height: 14,
    viewBox: '0 0 14 14',
    fill: 'none',
    'aria-hidden': true,
  } as const;
  switch (status) {
    case 'new':
      return (
        <svg {...common}>
          <circle
            cx="7"
            cy="7"
            r="5.5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeDasharray="2.6 2.1"
          />
        </svg>
      );
    case 'draft':
      return (
        <svg {...common}>
          <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.6" />
          <path d="M7 3.5a3.5 3.5 0 0 1 0 7z" fill="currentColor" />
        </svg>
      );
    case 'waiting':
      return (
        <svg {...common}>
          <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.6" />
          <path d="M7 4v3.2l2 1.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case 'verifying':
      return (
        <svg {...common}>
          <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.6" />
          <circle cx="7" cy="7" r="2" fill="currentColor" />
        </svg>
      );
    case 'closed':
      return (
        <svg {...common}>
          <circle cx="7" cy="7" r="6.2" fill="currentColor" />
          <path
            d="M4.3 7.2l1.9 1.9 3.5-3.6"
            stroke="#fff"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'accepted':
      return (
        <svg {...common}>
          <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.6" />
          <path d="M4.5 7h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
  }
}

const SEV_COLOURS = ['var(--faint)', 'var(--amber)', 'var(--red)'] as const;

/** Prototype `sevI`: three bars, lit up to the level. */
export function SeverityIcon({ level }: { level: Severity }) {
  return (
    <svg
      className="sevi"
      width="14"
      height="14"
      viewBox="0 0 14 14"
      role="img"
      aria-label={`${SEVERITY_UI[level].label} severity`}
    >
      {[0, 1, 2].map((i) => {
        const h = 3 + i * 3.5;
        return (
          <rect
            key={i}
            x={1 + i * 4.5}
            y={13 - h}
            width="3"
            height={h}
            rx="1"
            fill={i <= level ? SEV_COLOURS[level] : 'var(--line)'}
          />
        );
      })}
    </svg>
  );
}

export function severityColour(level: Severity): string {
  return SEV_COLOURS[level];
}

/** The small chevron after the user's name (`CH`). */
export function Chevron() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m3 4.5 3 3 3-3" />
    </svg>
  );
}

/** The card header chevron; `.chev.open` rotates it. */
export function CardChevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`chev${open ? ' open' : ''}`}
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export function SearchIcon() {
  return (
    <svg
      className="srch-i"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}
